import { Component, type ErrorInfo, type ReactNode } from "react";

import { isChunkLoadError, reloadForNewVersion } from "@/lib/chunkReload";

type Props = { children: ReactNode };
type State = { hasError: boolean; reloading?: boolean };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    // Falta un JS de una versión anterior (acaba de publicarse otra): se
    // recarga sola en vez de enseñar el error (src/lib/chunkReload.ts).
    if (isChunkLoadError(error) && reloadForNewVersion()) return { hasError: true, reloading: true };
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (this.state.reloading) return;
    // Bajo demanda, como el arranque de Sentry en main.tsx.
    void import("@/lib/sentry").then(({ initSentry, captureException }) => {
      initSentry();
      captureException(error, {
        contexts: { react: { componentStack: info.componentStack } },
      });
    });
    if (import.meta.env.DEV) {
      console.error("[ErrorBoundary]", error, info);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.reloading) return null;

    return (
      <div className="min-h-screen flex items-center justify-center bg-rc-offwhite px-6">
        <div className="max-w-md w-full text-center space-y-6">
          <h1 className="font-display text-4xl md:text-5xl tracking-[-0.03em] text-rc-navy">
            Algo ha ido mal
          </h1>
          <p className="text-rc-navy/70 leading-relaxed">
            Se ha producido un error inesperado. Prueba a recargar la página; si
            el problema persiste, vuelve al inicio y escríbenos.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="data-voice inline-flex items-center justify-center rounded-full border-2 border-rc-navy bg-rc-lime px-6 py-3 text-xs font-bold text-rc-navy transition-[transform,box-shadow] duration-200 ease-spring hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-hard-sm active:translate-x-0 active:translate-y-0 active:shadow-none active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2"
            >
              Recargar
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="data-voice inline-flex items-center justify-center rounded-full border-2 border-rc-navy px-6 py-3 text-xs font-bold text-rc-navy transition-[background-color,transform] duration-200 hover:bg-rc-lime active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2"
            >
              Ir al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }
}
